import { useState, useMemo, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Combobox } from "@/components/ui/combobox";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Table2, FileText, Send, Download, ClipboardPaste, CheckCircle2 } from "lucide-react";

const NL_MONTHS = ["januari", "februari", "maart", "april", "mei", "juni", "juli", "augustus", "september", "oktober", "november", "december"];
const nextMonthName = () => NL_MONTHS[(new Date().getMonth() + 1) % 12];
const todayNL = () => new Date().toLocaleDateString("nl-NL", { day: "numeric", month: "long", year: "numeric" });
const eur = (n) => "€ " + (Number(n) || 0).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const WIK_MAX = 6775, WIK_MIN = 40;
// Wettelijke incassokosten-staffel (Besluit BIK) — kaal bedrag over de hoofdsom
function wikCosts(hoofdsom) {
  let rem = Number(hoofdsom) || 0;
  if (rem <= 0) return 0;
  const tiers = [[2500, 0.15], [2500, 0.10], [5000, 0.05], [190000, 0.01], [Infinity, 0.005]];
  let fee = 0;
  for (const [cap, rate] of tiers) { const part = Math.min(rem, cap); fee += part * rate; rem -= part; if (rem <= 0) break; }
  fee = Math.min(Math.max(fee, WIK_MIN), WIK_MAX);
  return Math.round(fee * 100) / 100;
}

const LETTERS = [
  { v: "venster", l: "Huurincasso – ingebrekestelling & sommatie (VENSTER)" },
  { v: "aanmaning14", l: "14-dagen brief – laatste aanmaning (WIK)" },
];

function buildBody(type, ctx) {
  const { naam, adres, postcode, plaats, geachte, bedrijf, kenmerk, datum, komendeMaand, hoofdsom, rente, incasso, extra, komendeMaandBedrag, total } = ctx;
  const head = `AANTEKENEN\n${naam}\n${adres}\n${postcode} ${plaats}\n\nOns kenmerk: ${kenmerk}\nPerceel: ${adres}\n`;
  if (type === "venster") {
    return (
`${head}\nBetreft: ingebrekestelling & sommatie huurachterstand\n\n${datum}\n\n${geachte}\n\n` +
`Wij hebben u al eerder aangemaand, maar dat heeft helaas niet geresulteerd in uw volledige betaling en/of nadere betalingsafspraken. Op verzoek van Liv Residential en ${bedrijf}, de eigenaar van bovengenoemde woning, gaan wij over tot incasso van uw huurachterstand. Wij stellen u thans in gebreke en sommeren u om binnen 5 dagen na vandaag de volledige huurachterstand vermeerderd met de rente en de incassokosten te voldoen, uitsluitend door overboeking naar NL27ABNA0563943912 ten name van Stichting Praktisch Debiteurenbeheer onder vermelding van bovengenoemd kenmerk. U bent thans een bedrag verschuldigd ad ${total} bestaande uit de huurachterstand tot en met heden (${hoofdsom}), de rente (${rente}) en de incassokosten (${incasso}). Indien u niet binnen vijf dagen na heden het volledige bedrag voldoet, dan zullen wij direct rechtsmaatregelen tegen u treffen, waarbij naast uw huurachterstand en incassokosten ook de gerechtelijke kosten, de deurwaarderskosten en het griffierecht vermeerderd met de rente tot het moment van algehele betaling geheel voor uw rekening zullen komen.\n\n` +
`Incassokosten worden alléén gerekend over de hoofdsom ad ${hoofdsom}. De hiervoor genoemde incassokosten zullen bovendien verhoogd worden met een bedrag ad ${extra}, indien u niet binnen 14 dagen aanvangende de dag na ontvangst van deze ingebrekestelling ook de nieuw opgekomen huurschuld ad ${komendeMaandBedrag} betreffende de maand ${komendeMaand} voldoet. Let op: deze incassokosten voor de maand ${komendeMaand} kunt u nu dus nog voorkomen!\n\n` +
`In geval van een juridische procedure zal bovengenoemde vordering worden verhoogd met zowel de gerechtelijke als buitengerechtelijke kosten, te vermeerderen met de wettelijke rente tot het moment van algehele betaling. Als u voornemens bent om het op een juridische procedure aan te laten komen, dan verzoeken wij u om alvast uw verhinderdata en die van uw gemachtigde aan ons door te geven voor het eventueel in te plannen kortgeding.\n\n` +
`Wij vertrouwen erop dat u het niet zo ver zult laten komen en verblijven,\n\nMet voorname groet,\n\nPraktisch-Debiteurenbeheer\nRichard van Herpen\nE. info@praktisch-debiteurenbeheer.nl\nT. 0297-769085\n\n` +
`Ons telefonisch spreekuur is uitsluitend tussen 14:00 – 16:00 uur. Indien u niet in de gelegenheid bent om gedurende het spreekuur te bellen, verzoeken wij u per e-mail een belafspraak te maken.\n\n` +
`Overzicht openstaande huren / rente & incassokosten & na het verstrijken van de 14 dagen termijn extra te betalen incassokosten:\n\n` +
`Art. 6:44 lid 1 BW bepaalt dat uw betaling eerst ziet op buitengerechtelijke kosten en wettelijke rente en daarna pas op aflossing van de hoofdsom. Beheerder en verhuurder kunnen de BTW over de incassokosten niet verrekenen, vandaar dat de BTW bij u in rekening wordt gebracht. Bij 3 maanden of herhaaldelijke huurachterstand vragen wij de rechter om ontbinding van de huurovereenkomst en ontruiming van uw woning, tenzij wij alsnog spoedig een minnelijke oplossing kunnen vinden.`
    );
  }
  return (
`${head}\nBetreft: Laatste aanmaning huurachterstand ${adres} met aanzegging rente en kosten (WIK)\n\n${datum}\n\n${geachte}\n\n` +
`Uit de huuradministratie van ${bedrijf} blijkt dat u een huurachterstand heeft, hieronder gespecificeerd. De met u afgesproken betalingsregeling is niet correct nagekomen waardoor deze is vervallen. Uw huurachterstand is om die reden ter incasso aan ons kantoor overgedragen. U krijgt de gelegenheid om uw huurachterstand nu nog zonder incassokosten te betalen. Indien wij uw betaling niet uiterlijk binnen 14 dagen ontvangen, aanvangende de dag nadat deze brief bij u is bezorgd, dan zal uw huurschuld worden verhoogd met incassokosten. De incassokosten bedragen in uw geval ${incasso} inclusief 21% BTW (verhuurder en beheerder kunnen de BTW niet verrekenen). U kunt deze kosten nu dus nog voorkomen door binnen 14 dagen uw huurachterstand volledig te betalen.\n\n` +
`De incassokosten worden alléén gerekend over de hoofdsom ad ${hoofdsom} (zijnde de huurachterstand volgens onderstaand overzicht). Wij verzoeken u dringend om direct tot betaling van het openstaande bedrag ad ${hoofdsom} plus de gevallen rente ad ${rente} over te gaan, uitsluitend door overboeking naar ABN-AMRO NL27ABNA0563943912 ten name van Stichting Praktisch Debiteurenbeheer onder vermelding van uw naam & bovengenoemd kenmerk.\n\n` +
`Indien uw betaling achterwege blijft, zullen wij zonder nadere ingebrekestelling overgaan tot dagvaarding, waarbij alle kosten (zowel gerechtelijk als buitengerechtelijk) voor uw rekening komen. Bent u voornemens het op een procedure te laten aankomen, geef dan alvast uw verhinderdata en die van uw gemachtigde door.\n\n` +
`In afwachting van uw directe betaling verblijven wij,\n\nMet voorname groet,\n\nPraktisch Debiteurenbeheer\nRichard van Herpen\nT. 0297-769085\nE. Team@praktisch-debiteurenbeheer.nl\n\n` +
`Overzicht openstaande huren en rente & na het verstrijken van de 14 dagen termijn te betalen incassokosten:\n\n` +
`Art. 6:44 lid 1 BW bepaalt dat uw betaling eerst ziet op buitengerechtelijke kosten en wettelijke rente en daarna pas op aflossing van de hoofdsom. De rente over uw betalingsachterstand loopt door tot het moment van algehele betaling. Bij 3 maanden of herhaaldelijke huurachterstand vragen wij de rechter om ontbinding van de huurovereenkomst en ontruiming van uw woning, tenzij wij alsnog met spoed een goede minnelijke oplossing kunnen vinden.`
  );
}

export default function IncassoLetterFlow({ tenants, clients, initialTenantId = "", initialType = "", initialFrom = "" }) {
  const [step, setStep] = useState(initialTenantId ? 1 : 0);
  const [type, setType] = useState(initialType || "venster");
  const [tenantId, setTenantId] = useState(initialTenantId || "");
  const [rows, setRows] = useState([]);
  const [cols, setCols] = useState([]);
  const [headerNames, setHeaderNames] = useState({});
  const [savedLabels, setSavedLabels] = useState({});
  const [hoofdsom, setHoofdsom] = useState("");
  const [rente, setRente] = useState("");
  const [incasso, setIncasso] = useState("");
  const [extra, setExtra] = useState("");
  const [komendeMaandBedrag, setKomendeMaandBedrag] = useState("");
  const [kenmerk, setKenmerk] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [emailTo, setEmailTo] = useState("");
  const [letterId, setLetterId] = useState("");
  const [followupTask, setFollowupTask] = useState(true);
  const [followupDays, setFollowupDays] = useState("5");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/incasso-col-labels").then((r) => setSavedLabels(r.data.labels || {})).catch(() => {}); }, []);

  useEffect(() => {
    if (!initialFrom) return;
    api.get(`/letters/${initialFrom}/incasso-amounts`).then((r) => {
      const a = r.data || {};
      if (a.hoofdsom != null) setHoofdsom(String(a.hoofdsom));
      if (a.rente != null) setRente(String(a.rente));
      if (a.incasso != null) setIncasso(String(a.incasso));
      if (a.extra != null) setExtra(String(a.extra));
      if (a.komendeMaandBedrag != null) setKomendeMaandBedrag(String(a.komendeMaandBedrag));
      if (Object.keys(a).length) toast.success("Bedragen overgenomen uit de vorige incassobrief — controleer en pas ze evt. aan");
    }).catch(() => {});
  }, [initialFrom]);

  const tenant = useMemo(() => tenants.find((t) => t.id === tenantId), [tenants, tenantId]);
  const client = useMemo(() => clients.find((c) => c.id === (tenant?.client_id)), [clients, tenant]);
  const total = (Number(hoofdsom) || 0) + (Number(rente) || 0) + (Number(incasso) || 0);
  const headerLabel = (i) => (headerNames[i] ?? (rows[0]?.[i] ?? ""));
  const displayRows = useMemo(() => {
    if (!rows.length) return [];
    const header = cols.map((i) => headerLabel(i));
    const dataRows = rows.slice(1).map((r) => cols.map((i) => r[i] ?? ""));
    return [header, ...dataRows];
  }, [rows, cols, headerNames]);
  const toggleCol = (i) => setCols((cs) => cs.includes(i) ? cs.filter((x) => x !== i) : [...cs, i].sort((a, b) => a - b));
  const setHeaderName = (i, v) => setHeaderNames((h) => ({ ...h, [i]: v }));
  const applyWik = () => {
    const fee = wikCosts(hoofdsom);
    if (!fee) { toast.error("Vul eerst de hoofdsom in"); return; }
    setIncasso(String(Math.round(fee * 1.21 * 100) / 100));
    toast.success(`Incassokosten (WIK): ${eur(fee)} + 21% btw = ${eur(fee * 1.21)}`);
  };
  const toStep2 = async () => {
    if (tenant && !hoofdsom && !rente) {
      try {
        const r = await api.get(`/tenants/${tenantId}/incasso-suggestion`);
        if (r.data.hoofdsom) setHoofdsom(String(r.data.hoofdsom));
        if (r.data.rente) setRente(String(r.data.rente));
        if (r.data.incasso) setIncasso(String(r.data.incasso));
        if (r.data.hoofdsom || r.data.rente) toast.success("Bedragen voorgevuld uit openstaande termijnen — controleer ze");
      } catch { /* laat leeg */ }
    }
    setStep(2);
  };

  const onPaste = (e) => {
    const html = e.clipboardData.getData("text/html");
    const text = e.clipboardData.getData("text/plain");
    let parsed = [];
    if (html && html.toLowerCase().includes("<table")) {
      const docp = new DOMParser().parseFromString(html, "text/html");
      parsed = [...docp.querySelectorAll("tr")].map((tr) => [...tr.querySelectorAll("th,td")].map((td) => td.textContent.trim()));
    } else if (text) {
      parsed = text.split(/\r?\n/).filter((l) => l.trim()).map((l) => l.split("\t").map((c) => c.trim()));
    }
    parsed = parsed.filter((r) => r.some((c) => c));
    if (parsed.length) {
      const n = Math.max(...parsed.map((r) => r.length));
      setRows(parsed); setCols([...Array(n).keys()]);
      const hn = {};
      (parsed[0] || []).forEach((h, i) => { const key = (h || "").trim(); if (key && savedLabels[key]) hn[i] = savedLabels[key]; });
      setHeaderNames(hn);
      e.preventDefault(); toast.success(`${parsed.length} rij(en) uit de tabel ingelezen`);
    }
    else toast.error("Geen tabel herkend — kopieer het bereik in Excel en plak opnieuw");
  };

  const buildPreview = async () => {
    if (!tenant) { toast.error("Kies een debiteur"); return; }
    setBusy(true);
    try {
      const kr = await api.get("/incasso-kenmerk");
      const kk = kr.data.kenmerk;
      setKenmerk(kk);
      const mapping = { ...savedLabels };
      cols.forEach((i) => { const key = (rows[0]?.[i] || "").trim(); const val = (headerNames[i] || "").trim(); if (key && val && val !== key) mapping[key] = val; });
      setSavedLabels(mapping);
      api.put("/incasso-col-labels", { labels: mapping }).catch(() => {});
      const ctx = {
        naam: `${tenant.first_name || ""} ${tenant.last_name || ""}`.trim(),
        adres: `${tenant.naw_street || ""} ${tenant.naw_number || ""}`.trim(),
        postcode: tenant.naw_postal || "", plaats: tenant.naw_city || "",
        geachte: `Geachte heer/mevrouw ${tenant.last_name || ""},`.replace(/\s+,/, ","),
        bedrijf: client?.name || client?.company || "de verhuurder",
        kenmerk: kk, datum: todayNL(), komendeMaand: nextMonthName(),
        hoofdsom: eur(hoofdsom), rente: eur(rente), incasso: eur(incasso), extra: eur(extra),
        komendeMaandBedrag: eur(komendeMaandBedrag), total: eur(total),
      };
      setSubject(type === "venster" ? "Ingebrekestelling & sommatie huurachterstand" : "Laatste aanmaning huurachterstand (WIK)");
      setBody(buildBody(type, ctx));
      setEmailTo(tenant.email1 || tenant.email2 || "");
      setStep(3);
    } catch { toast.error("Kenmerk genereren mislukt"); }
    finally { setBusy(false); }
  };

  const save = async (sendEmail) => {
    setBusy(true);
    try {
      const to = emailTo.split(",").map((s) => s.trim()).filter(Boolean);
      const payload = { recipient_type: "debiteur", tenant_id: tenantId, set_type: "aanmaning", tone: "Formeel Juridisch",
        subject, body, spec_rows: displayRows, send_email: sendEmail, to, kenmerk, incasso_type: type,
        followup_days: followupTask ? (Number(followupDays) || 0) : 0,
        incasso_amounts: { hoofdsom: Number(hoofdsom) || 0, rente: Number(rente) || 0, incasso: Number(incasso) || 0, extra: Number(extra) || 0, komendeMaandBedrag: Number(komendeMaandBedrag) || 0 } };
      const r = await api.post("/letter-generator/save", payload);
      setLetterId(r.data.letter_id);
      toast.success(sendEmail ? `Brief verzonden naar ${(r.data.recipients || []).join(", ")}` : "Brief opgeslagen en geregistreerd");
      if (followupTask && (Number(followupDays) || 0) > 0) toast.success(`Opvolgtaak aangemaakt — nabellen over ${Number(followupDays)} dagen`);
      setStep(4);
    } catch (e) { toast.error(e?.response?.data?.detail || "Opslaan mislukt"); }
    finally { setBusy(false); }
  };

  const downloadPdf = async () => {
    try {
      const r = await api.get(`/letters/${letterId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `${kenmerk || "incassobrief"}.pdf`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("PDF downloaden mislukt"); }
  };

  const tenantOptions = tenants.map((t) => ({ value: t.id, label: `${t.first_name} ${t.last_name}` }));

  return (
    <div className="space-y-5" data-testid="incasso-flow">
      {step === 0 && (
        <div className="space-y-4">
          <div>
            <Label>Briefsoort</Label>
            <div className="grid sm:grid-cols-2 gap-2 mt-1">
              {LETTERS.map((L) => (
                <button key={L.v} type="button" onClick={() => setType(L.v)} data-testid={`incasso-type-${L.v}`}
                  className={`text-left border rounded-xl p-3 text-sm transition-colors ${type === L.v ? "ring-2 ring-indigo-500 border-indigo-400 bg-indigo-50/50" : "hover:border-indigo-300"}`}>
                  <FileText className="h-4 w-4 text-indigo-600 mb-1" /> {L.l}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label>Debiteur</Label>
            <Combobox testId="incasso-tenant-select" value={tenantId} onChange={setTenantId} placeholder="Selecteer debiteur" options={tenantOptions} />
          </div>
          <div className="flex justify-end"><Button disabled={!tenantId} onClick={() => setStep(1)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="incasso-next-0">Volgende <ArrowRight className="h-4 w-4 ml-2" /></Button></div>
        </div>
      )}

      {step === 1 && (
        <div className="space-y-4">
          <div>
            <Label className="flex items-center gap-2"><ClipboardPaste className="h-4 w-4 text-amber-600" /> Plak hier de gele tabel uit Excel</Label>
            <p className="text-xs text-slate-400 mb-1">Kopieer het tabelbereik in Excel (Ctrl/Cmd+C) en klik hieronder, plak (Ctrl/Cmd+V). Alleen de waarden worden overgenomen in een strakke stijl.</p>
            <div contentEditable suppressContentEditableWarning onPaste={onPaste} data-testid="incasso-paste-box"
              className="min-h-[90px] border-2 border-dashed border-amber-300 bg-amber-50/40 rounded-xl p-3 text-sm text-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-400">
              {rows.length === 0 ? "Plak hier…" : ""}
            </div>
          </div>
          {rows.length > 0 && (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-2 items-center" data-testid="incasso-col-chooser">
                <span className="text-xs text-slate-500">Kolommen in de brief:</span>
                {rows[0].map((h, i) => (
                  <button key={i} type="button" onClick={() => toggleCol(i)} data-testid={`incasso-col-${i}`}
                    className={`px-2 py-0.5 rounded-full text-xs border transition-colors ${cols.includes(i) ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-500 hover:border-indigo-300"}`}>
                    {h || `Kolom ${i + 1}`}
                  </button>
                ))}
              </div>
              {cols.length > 0 && (
                <div className="flex flex-wrap gap-3 pt-1" data-testid="incasso-col-rename">
                  {cols.map((i) => (
                    <div key={i} className="flex flex-col gap-1">
                      <span className="text-[11px] text-slate-400">Kolomtitel{rows[0]?.[i] ? ` (was: ${rows[0][i]})` : ` ${i + 1}`}</span>
                      <Input value={headerNames[i] ?? (rows[0]?.[i] ?? "")} onChange={(e) => setHeaderName(i, e.target.value)} className="h-8 w-40 text-sm" data-testid={`incasso-col-rename-${i}`} />
                    </div>
                  ))}
                </div>
              )}
              <div className="border rounded-xl overflow-hidden" data-testid="incasso-table-preview">
                <table className="w-full text-sm">
                  <tbody>
                    {displayRows.map((r, i) => (
                      <tr key={i} className={i === 0 ? "bg-slate-100 font-semibold" : "border-t"}>
                        {r.map((c, j) => <td key={j} className="px-3 py-1.5">{c}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <div className="flex justify-between"><Button variant="outline" onClick={() => setStep(0)}><ArrowLeft className="h-4 w-4 mr-2" /> Terug</Button><Button disabled={rows.length === 0} onClick={toStep2} className="bg-indigo-600 hover:bg-indigo-700" data-testid="incasso-next-1">Volgende <ArrowRight className="h-4 w-4 ml-2" /></Button></div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <Label>Bedragen</Label>
          <div className="grid sm:grid-cols-3 gap-3">
            <div><Label className="text-xs">Hoofdsom (huurachterstand)</Label><Input type="number" value={hoofdsom} onChange={(e) => setHoofdsom(e.target.value)} data-testid="incasso-hoofdsom" /></div>
            <div><Label className="text-xs">Rente</Label><Input type="number" value={rente} onChange={(e) => setRente(e.target.value)} data-testid="incasso-rente" /></div>
            <div><Label className="text-xs">Incassokosten (incl. 21% btw)</Label><Input type="number" value={incasso} onChange={(e) => setIncasso(e.target.value)} data-testid="incasso-incasso" /><button type="button" onClick={applyWik} className="text-[11px] text-indigo-600 hover:underline mt-1" data-testid="incasso-wik-btn">Bereken volgens WIK-staffel</button></div>
            {type === "venster" && <div><Label className="text-xs">Extra incassokosten (bij niet betalen)</Label><Input type="number" value={extra} onChange={(e) => setExtra(e.target.value)} data-testid="incasso-extra" /></div>}
            {type === "venster" && <div><Label className="text-xs">Huur komende maand ({nextMonthName()})</Label><Input type="number" value={komendeMaandBedrag} onChange={(e) => setKomendeMaandBedrag(e.target.value)} data-testid="incasso-komende" /></div>}
          </div>
          <div className="text-sm bg-indigo-50 border border-indigo-100 rounded-lg p-3" data-testid="incasso-total">Totaal verschuldigd (hoofdsom + rente + incasso): <b>{eur(total)}</b></div>
          <div className="flex justify-between"><Button variant="outline" onClick={() => setStep(1)}><ArrowLeft className="h-4 w-4 mr-2" /> Terug</Button><Button disabled={busy} onClick={buildPreview} className="bg-indigo-600 hover:bg-indigo-700" data-testid="incasso-preview-btn">Voorbeeld <ArrowRight className="h-4 w-4 ml-2" /></Button></div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between"><Label>Voorbeeld — {subject}</Label><Badge kenmerk={kenmerk} /></div>
          <div className="border rounded-xl p-4 bg-white max-h-[45vh] overflow-y-auto text-sm text-slate-700 whitespace-pre-wrap" data-testid="incasso-preview">{body}</div>
          {rows.length > 0 && (
            <div className="border rounded-xl overflow-hidden">
              <div className="px-3 py-1.5 text-xs font-semibold bg-amber-100 text-amber-800 flex items-center gap-1"><Table2 className="h-3.5 w-3.5" /> Ingevoegde tabel</div>
              <table className="w-full text-sm"><tbody>{displayRows.map((r, i) => (<tr key={i} className={i === 0 ? "bg-slate-100 font-semibold" : "border-t"}>{r.map((c, j) => <td key={j} className="px-3 py-1.5">{c}</td>)}</tr>))}</tbody></table>
            </div>
          )}
          <div>
            <Label className="text-xs">E-mailadres(sen) — optioneel, komma-gescheiden</Label>
            <Input value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="naam@voorbeeld.nl" data-testid="incasso-email-to" />
          </div>
          <div className="flex items-center gap-3 border rounded-xl p-3 bg-slate-50" data-testid="incasso-followup">
            <Checkbox id="incasso-followup-check" checked={followupTask} onCheckedChange={(v) => setFollowupTask(!!v)} data-testid="incasso-followup-check" />
            <Label htmlFor="incasso-followup-check" className="text-sm font-normal cursor-pointer">Opvolgtaak aanmaken — nabellen over</Label>
            <Input type="number" min="1" value={followupDays} onChange={(e) => setFollowupDays(e.target.value)} disabled={!followupTask} className="h-8 w-16 text-sm" data-testid="incasso-followup-days" />
            <span className="text-sm text-slate-500">dagen</span>
          </div>
          <div className="flex flex-wrap justify-between gap-2">
            <Button variant="outline" onClick={() => setStep(2)}><ArrowLeft className="h-4 w-4 mr-2" /> Terug</Button>
            <div className="flex gap-2">
              <Button variant="outline" disabled={busy} onClick={() => save(false)} data-testid="incasso-save"><FileText className="h-4 w-4 mr-2" /> Opslaan</Button>
              <Button className="bg-indigo-600 hover:bg-indigo-700" disabled={busy || !emailTo.trim()} onClick={() => save(true)} data-testid="incasso-send"><Send className="h-4 w-4 mr-2" /> Opslaan & e-mailen</Button>
            </div>
          </div>
        </div>
      )}

      {step === 4 && (
        <div className="space-y-4 text-center py-4" data-testid="incasso-done">
          <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" />
          <h3 className="font-semibold text-slate-800">Brief klaar ({kenmerk})</h3>
          <div className="flex justify-center gap-2">
            <Button onClick={downloadPdf} className="bg-indigo-600 hover:bg-indigo-700" data-testid="incasso-download-pdf"><Download className="h-4 w-4 mr-2" /> PDF downloaden</Button>
            <Button variant="outline" onClick={() => { setStep(0); setRows([]); setCols([]); setHeaderNames({}); setHoofdsom(""); setRente(""); setIncasso(""); setExtra(""); setKomendeMaandBedrag(""); setLetterId(""); }} data-testid="incasso-new">Nieuwe incassobrief</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function Badge({ kenmerk }) {
  return <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600" data-testid="incasso-kenmerk">Kenmerk: {kenmerk}</span>;
}
